import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  TextField,
  Grid,
  Box,
  IconButton,
  List,
  ListItem,
  Checkbox,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMaterialUIController } from "context";
import CTLDialogContent from "../styles/CTLDialogContent";
import MDButton from "components/MDButton";
import CTLNotification from "../CTLNotification";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import CTLSelect from "../CTLSelect";
import CTLMultipleSelect from "../CTLMultipleSelect";

const SORT_DIRECTIONS = ["Descending", "Ascending"];

const getOperators = (dataType) => {
  switch (dataType) {
    case "number":
    case "integer":
    case "date":
      return ["is", "greaterthan", "lessthan", "between"];
    case "time": return ["between"];
    case "string":
    default:
      return ["is"];
  }
};

const getNativeInputType = (col) => {
  if (!col) return "text";
  if (col.dateTime) return "datetime-local";
  if (col.dataType === "date") return "date";
  if (col.dataType === "time") return "time";
  if (col.dataType === "integer" || col.dataType === "number") return "number";
  return "text";
};

function FilterValueInput({ column, value, onChange }) {
  const handle = (val) => onChange(val);

  if (column.controlType === "Select") {
    return (
      <CTLSelect
        name={column.accessor}
        value={value || ""}
        onChange={(e) => handle(e.target.value)}
        url={column.options?.url}
        options={column.options?.values || []}
        keyField={column.options?.keyField}
        keyDescription={column.options?.keyDescription}
        column={column}
      />
    );
  }

  if (column.controlType === "multipleSelect") {
    return (
      <CTLMultipleSelect
        name={column.accessor}
        value={Array.isArray(value) ? value : []}
        onChange={(e) => handle(e.target.value)}
        url={column.options?.url}
        options={column.options?.values || []}
        keyField={column.options?.keyField}
        keyDescription={column.options?.keyDescription}
      />
    );
  }

  return (
    <TextField
      fullWidth
      type={getNativeInputType(column)}
      value={value ?? ""}
      InputProps={{
        sx: {
          height: "55px",
          "& .MuiInputBase-input": {
            height: "55px",
            padding: "0 14px",
            boxSizing: "border-box",
          },
        },
      }}
      onChange={(e) => handle(e.target.value)}
    />
  );
}

export default function CTLReportFilterForAgent({
  openReportFilter,
  onCloseForm,
  columns,
  onSubmit,
}) {
  const reportColumns = columns?.filter((col) => col.allowReportColumn) || [];
  const reportFilterColumns = columns?.filter((col) => col.allowReportFilter) || [];
  const sortColumn = columns?.filter((col)=> col.allowReportSort) || [];
  const [filters, setFilters] = useState([]);
  const [newFilter, setNewFilter] = useState("");
  const [sorts, setSorts] = useState([
    { column: "", direction: "Descending" },
    { column: "", direction: "Descending" },
  ]);

  const [selectedColumns, setSelectedColumns] = useState([]); // start with []
  const [selectedColumnsList, setSelectedColumnsList] = useState(reportColumns);
  const [availableColumns] = useState(reportColumns);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedSelected, setSelectedSelected] = useState([]);

  const [controller] = useMaterialUIController();
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const { darkMode } = controller;

  useEffect(() => {
    if (columns?.length) {
      const allowed = columns.filter((c) => c.allowReportColumn);
      setSelectedColumns(allowed.map((c) => c.accessor));
      setSelectedColumnsList(allowed);
    }
  }, [columns]);

  const toggleSelection = (list, setList, accessor) => {
    setList((prev) =>
      prev.includes(accessor)
        ? prev.filter((a) => a !== accessor)
        : [...prev, accessor]
    );
  };

  const handleRemoveFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  // smart, multi-aware
  const handleFilterChange = (index, key, value) => {
    setFilters((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const col = columns.find((c) => c.accessor === f.field);
        const isMulti = col?.controlType === "multipleSelect";

        if (key === "operator" || key === "value2") {
          return { ...f, [key]: value };
        }

        if (isMulti) {
          return {
            ...f,
            value1: "",
            value3: Array.isArray(value) ? value : [],
          };
        }

        return { ...f, value1: value };
      })
    );
  };

  const handleSortChange = (index, key, value) => {
    setSorts((prev) => {
      const updated = [...prev];
      updated[index][key] = value;
      return updated;
    });
  };

  const handleColumnTransfer = (accessors, toSelected) => {
    if (toSelected) {
      const toMove = availableColumns.filter(
        (col) =>
          accessors.includes(col.accessor) &&
          !selectedColumnsList.some((sel) => sel.accessor === col.accessor)
      );

      if (toMove.length > 0) {
        const newRight = [...selectedColumnsList, ...toMove];
        setSelectedColumnsList(newRight);
        setSelectedColumns(newRight.map((c) => c.accessor));
      }
      setSelectedAvailable([]);
    } else {
      const remaining = selectedColumnsList.filter(
        (col) => !accessors.includes(col.accessor)
      );
      setSelectedColumnsList(remaining);
      setSelectedColumns(remaining.map((c) => c.accessor));
      setSelectedSelected([]);
    }
  };

  const handleAddAll = () => {
    const toMove = availableColumns.filter(
      (col) => !selectedColumnsList.some((c) => c.accessor === col.accessor)
    );
    const newRight = [...selectedColumnsList, ...toMove];
    setSelectedColumnsList(newRight);
    setSelectedColumns(newRight.map((c) => c.accessor));
    setSelectedAvailable([]);
  };

  const handleRemoveAll = () => {
    setSelectedColumnsList([]);
    setSelectedColumns([]);
    setSelectedSelected([]);
  };

  const moveColumn = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= selectedColumnsList.length) return;
    const updated = [...selectedColumnsList];
    const order = [...selectedColumns];
    const [movedCol] = updated.splice(fromIndex, 1);
    const [movedKey] = order.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedCol);
    order.splice(toIndex, 0, movedKey);
    setSelectedColumnsList(updated);
    setSelectedColumns(order);
  };

  const moveSelectedUp = () => {
    if (selectedSelected.length === 0) return;
    const idx = selectedColumnsList.findIndex(
      (c) => c.accessor === selectedSelected[0]
    );
    if (idx > 0) moveColumn(idx, idx - 1);
  };

  const moveSelectedDown = () => {
    if (selectedSelected.length === 0) return;
    const idx = selectedColumnsList.findIndex(
      (c) => c.accessor === selectedSelected[0]
    );
    if (idx < selectedColumnsList.length - 1) moveColumn(idx, idx + 1);
  };

  const normalizeFilters = (filters, columns) =>
    filters.map((f) => {
      const col = columns.find((c) => c.accessor === f.field);
      const isMulti = col?.controlType === "multipleSelect";
      if (!isMulti) return f;
      if (Array.isArray(f.value1)) {
        return {
          ...f,
          value1: "",
          value3: Array.isArray(f.value3) ? f.value3 : f.value1,
        };
      }
      return {
        ...f,
        value3: Array.isArray(f.value3) ? f.value3 : [],
      };
    });

  const handleAddFilter = (accessor, init = {}) => {
    const col = columns.find((c) => c.accessor === accessor);
    if (!col || filters.some((f) => f.field === accessor)) return;

    const isMulti = col.controlType === "multipleSelect";
    const defaultOperator =
      col.dataType === "time" ? "between" : init.operator || "is";

    const { value1, value2, value3 } = init;

    setFilters((prev) => [
      ...prev,
      {
        header: col.Header,
        field: accessor,
        dataType: col.dataType,
        operator: defaultOperator,
        value1: isMulti ? "" : value1 ?? "",
        value2: value2 ?? "",
        value3: Array.isArray(value3)
          ? value3
          : isMulti
          ? Array.isArray(value1)
            ? value1
            : []
          : [],
      },
    ]);

    setNewFilter("");
  };

  const handleSubmit = () => {
    const filteredSorts = sorts.filter((s) => s.column?.trim());
    if (selectedColumns.length > 0) {
      if (filteredSorts.length > 0) {
        const output = {
          selectedColumns,
          filters: normalizeFilters(filters, columns),
          sorts: filteredSorts,
        };

        onSubmit(output);
        // onClearForm();
        onCloseForm();
      } else {
        setNotify({
          isOpen: true,
          pagename: "Advance Filter",
          status: "Please select sort",
          type: "warning",
        });
      }
    } else {
      setNotify({
        isOpen: true,
        pagename: "Advance Filter",
        status: "Please select at least one column",
        type: "warning",
      });
    }
  };

  const onClearForm = () => {
    setSelectedColumns([]);
    setSelectedColumnsList([]);
    setSelectedAvailable([]);
    setSelectedSelected([]);
    setFilters([]);
    setSorts([
      { column: "", direction: "Descending" },
      { column: "", direction: "Descending" },
    ]);
  };

  return (
    <Dialog
      open={openReportFilter}
      onClose={onCloseForm}
      fullWidth
      maxWidth="lg"
      sx={(theme) => CTLDialogContent(theme, { darkMode })}
    >
      <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        Report Advanced Filter
      </DialogTitle>

      <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        {/* COLUMNS */}
        <Box border={1} borderRadius={2} p={2} mb={2}>
          <Typography variant="h6">Columns</Typography>
          <Accordion sx={(theme) => CTLDialogContent(theme, { darkMode })}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="text" />} />
            <AccordionDetails>
              <Box display="flex" justifyContent="space-between" mt={1}>
                {/* LEFT: Available */}
                <MDBox
                  border={1}
                  borderRadius={2}
                  sx={{ width: "42%", p: 1, minHeight: 300 }}
                >
                  <MDTypography
                    fontWeight="bold"
                    fontSize={14}
                    textAlign="center"
                  >
                    Available Columns
                  </MDTypography>
                  <List sx={{ maxHeight: 250, overflow: "auto" }}>
                    {availableColumns.map((col) => (
                      <ListItem key={col.accessor} button sx={{ pl: 1 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedAvailable.includes(col.accessor)}
                          tabIndex={-1}
                          disableRipple
                          sx={{ mr: 1 }}
                          onClick={() =>
                            toggleSelection(
                              selectedAvailable,
                              setSelectedAvailable,
                              col.accessor
                            )
                          }
                        />
                        <MDTypography>{col.Header}</MDTypography>
                      </ListItem>
                    ))}
                  </List>
                </MDBox>

                {/* CENTER: actions (add/remove + up/down) */}
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mx={1}
                >
                  <Tooltip title="Add Selected">
                    <IconButton
                      color="text"
                      onClick={() =>
                        handleColumnTransfer(selectedAvailable, true)
                      }
                      disabled={selectedAvailable.length === 0}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Remove Selected">
                    <IconButton
                      color="text"
                      onClick={() =>
                        handleColumnTransfer(selectedSelected, false)
                      }
                      disabled={selectedSelected.length === 0}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Add All">
                    <IconButton color="text" onClick={handleAddAll}>
                      <KeyboardDoubleArrowRightIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Remove All">
                    <IconButton color="text" onClick={handleRemoveAll}>
                      <KeyboardDoubleArrowLeftIcon />
                    </IconButton>
                  </Tooltip>

                  <Box
                    mt={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Tooltip title="Move Up">
                      <IconButton
                        color="text"
                        onClick={moveSelectedUp}
                        disabled={selectedSelected.length === 0}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Move Down">
                      <IconButton
                        color="text"
                        onClick={moveSelectedDown}
                        disabled={selectedSelected.length === 0}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* RIGHT: Selected */}
                <MDBox
                  border={1}
                  borderRadius={2}
                  sx={{ width: "42%", p: 1, minHeight: 300 }}
                >
                  <MDTypography
                    fontWeight="bold"
                    fontSize={14}
                    textAlign="center"
                  >
                    Selected Columns
                  </MDTypography>
                  <List sx={{ maxHeight: 250, overflow: "auto" }}>
                    {selectedColumnsList.map((col) => (
                      <ListItem key={col.accessor} button sx={{ pl: 1 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedSelected.includes(col.accessor)}
                          tabIndex={-1}
                          disableRipple
                          onClick={() =>
                            toggleSelection(
                              selectedSelected,
                              setSelectedSelected,
                              col.accessor
                            )
                          }
                          sx={{ mr: 1 }}
                        />
                        <MDTypography>{col.Header}</MDTypography>
                      </ListItem>
                    ))}
                  </List>
                </MDBox>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* FILTERS */}
        <Box border={1} borderRadius={2} p={2} mb={2}>
          <Typography variant="h6">Filters</Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={3}>
              <Select
                fullWidth
                sx={{ height: "40px" }}
                value={newFilter}
                displayEmpty
                onChange={(e) => handleAddFilter(e.target.value)}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 200 } },
                }}
              >
                <MenuItem value="" disabled>
                  Add Filter
                </MenuItem>
                {reportFilterColumns.map((col) => (
                  <MenuItem key={col.accessor} value={col.accessor}>
                    {col.Header}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>

          {filters.map((filter, index) => {
            const col = columns.find((c) => c.accessor === filter.field);
            const operators = getOperators(col?.dataType);
            const isBetween = filter.operator === "between";
            const isMultipleSelect = col?.controlType === "multipleSelect";
            const valueKey = isMultipleSelect ? "value3" : "value1";

            return (
              <Grid
                container
                spacing={2}
                key={index}
                alignItems="center"
                mt={1}
              >
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    value={filter.header}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        height: "55px",
                        "& .MuiInputBase-input": {
                          height: "55px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <Select
                    fullWidth
                    sx={{ height: "55px" }}
                    value={filter.operator}
                    onChange={(e) =>
                      handleFilterChange(index, "operator", e.target.value)
                    }
                  >
                    {operators.map((op) => (
                      <MenuItem key={op} value={op}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={isBetween ? 2.5 : 5}>
                  <FilterValueInput
                    column={col}
                    value={filter[valueKey]}
                    onChange={(val) => handleFilterChange(index, valueKey, val)}
                  />
                </Grid>

                {isBetween && (
                  <Grid item xs={2.5}>
                    <FilterValueInput
                      column={col}
                      value={filter.value2}
                      onChange={(val) =>
                        handleFilterChange(index, "value2", val)
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={1}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveFilter(filter.field)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        </Box>

        {/* SORTS */}
        <Box border={1} borderRadius={2} p={2} mb={2}>
          <Typography variant="h6">Sort</Typography>
          <Grid container spacing={2}>
            {(sortColumn.length > 1 ? [0, 1] : [0]).map((index) => (
              <React.Fragment key={index}>
                <Grid item xs={6}>
                  <Select
                    fullWidth
                    displayEmpty
                    sx={{ height: "55px" }}
                    value={sorts[index]?.column || ""}
                    onChange={(e) =>
                      handleSortChange(index, "column", e.target.value)
                    }
                    MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                  >
                    <MenuItem value="">Select column</MenuItem>
                    {sortColumn.map((col) => (
                      <MenuItem key={col.accessor} value={col.accessor}>
                        {col.Header}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <Select
                    fullWidth
                    sx={{ height: "55px" }}
                    value={sorts[index]?.direction || "Descending"}
                    onChange={(e) =>
                      handleSortChange(index, "direction", e.target.value)
                    }
                    MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                  >
                    {SORT_DIRECTIONS.map((dir) => (
                      <MenuItem key={dir} value={dir}>
                        {dir}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        <MDButton onClick={onCloseForm} color="secondary" variant="contained">
          Cancel
        </MDButton>
        <MDButton onClick={onClearForm} color="error" variant="contained">
          Clear Filter
        </MDButton>
        <MDButton onClick={handleSubmit} color="success" variant="contained">
          Download CSV
        </MDButton>
      </DialogActions>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </Dialog>
  );
}
